// src/pages/MuadilYonetim.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Trash2,
  Edit2,
  Plus,
  X,
  Save,
  Search,
  RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import './MuadilYonetim.css';


const varsayilanKategoriler = [
  'YAĞ FİLTRESİ',
  'HAVA FİLTRESİ',
  'SEPERATÖR',
  'MPV',
  'OIL STOP VALF',
  'ÇEKVALF',
  'TERMOSTAT',
  'KEÇE',
  'KEÇE BURCU',
  'ERKEK ROTOR ÖN',
  'ERKEK ROTOR ARKA',
  'DİŞİ ROTOR ÖN',
  'DİŞİ ROTOR ARKA',
  'ELEKTRİK MOTOR R.',
  'FAN MOTOR R.',
  'SU TAHLİYE (DRAIN VALF)',
  'KURUTUCU VE HAT FİLTRE',
  'CAPLİN-KAYIŞ',
  'EMİŞ VALFİ',
  'REGULATING VALF'
];


const normalizeMetin = (deger) => {
  if (!deger) return '';


  return deger
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
};


const normalizeKategori = normalizeMetin;


const normalizeKod = normalizeMetin;


const benzersizId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;


const bosParca = () => ({
  id: benzersizId(),
  kod: '',
  tedarikci: '',
  fiyat: '',
  adet: 1
});


const gecerliTarih = (tarih) => {
  if (!tarih) return false;


  return !Number.isNaN(new Date(tarih).getTime());
};


const MuadilYonetim = () => {
  const [muadiller, setMuadiller] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [arama, setArama] = useState('');
  const [seciliKategori, setSeciliKategori] = useState('tum');
  const [modalAcik, setModalAcik] = useState(false);
  const [duzenlemeModu, setDuzenlemeModu] = useState(false);
  const [aktifGrup, setAktifGrup] = useState(null);
  const [formKategori, setFormKategori] = useState('');
  const [formAciklama, setFormAciklama] = useState('');
  const [formParcalar, setFormParcalar] = useState([bosParca()]);
  const [yeniParcaKod, setYeniParcaKod] = useState('');
  const [yeniParcaTedarikci, setYeniParcaTedarikci] = useState('');
  const [yeniParcaFiyat, setYeniParcaFiyat] = useState('');
  const [yeniParcaAdet, setYeniParcaAdet] = useState('1');


  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [silinenGrupId, setSilinenGrupId] = useState(null);


  const [guncellemeModalAcik, setGuncellemeModalAcik] = useState(false);
  const [etkilenenKayitlar, setEtkilenenKayitlar] = useState({
    sayi: 0,
    kayitlar: []
  });
  const [guncellemeYukleniyor, setGuncellemeYukleniyor] = useState(false);
  const [sonKaydedilenGrup, setSonKaydedilenGrup] = useState(null);
  const [senkronizeModu, setSenkronizeModu] = useState(false);


  const modalRef = useRef(null);


  const fetchMuadiller = useCallback(async () => {
    try {
      setYukleniyor(true);


      const res = await fetch('/api/muadiller');


      if (!res.ok) {
        throw new Error(`Muadil verileri alınamadı: ${res.status}`);
      }


      const data = await res.json();


      setMuadiller(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Muadil çekme hatası:', err);
      toast.error('❌ Muadil verileri yüklenemedi. Sayfayı yenileyin.');
      setMuadiller([]);
    } finally {
      setYukleniyor(false);
    }
  }, []);


  useEffect(() => {
    fetchMuadiller();
  }, [fetchMuadiller]);


  useEffect(() => {
    if (!modalAcik) return;


    const ilkInput = modalRef.current?.querySelector('select, input');


    ilkInput?.focus();
  }, [modalAcik]);


  const filtrelenmisMuadiller = useMemo(() => {
    const aramaMetni = arama.toLowerCase().trim();


    return muadiller.filter((grup) => {
      const kategoriUyumu =
        seciliKategori === 'tum' ||
        normalizeKategori(grup.kategori) ===
          normalizeKategori(seciliKategori);


      const parcalar = Array.isArray(grup.parcalar) ? grup.parcalar : [];


      const aramaUyumu =
        !aramaMetni ||
        (grup.aciklama ?? '')
          .toLowerCase()
          .includes(aramaMetni) ||
        parcalar.some(
          (parca) =>
            (parca.kod ?? '')
              .toLowerCase()
              .includes(aramaMetni) ||
            (parca.tedarikci ?? '')
              .toLowerCase()
              .includes(aramaMetni)
        );


      return kategoriUyumu && aramaUyumu;
    });
  }, [muadiller, seciliKategori, arama]);


  const handleYeniAc = useCallback(() => {
    setDuzenlemeModu(false);
    setAktifGrup(null);
    setFormKategori('');
    setFormAciklama('');
    setFormParcalar([bosParca()]);
    setModalAcik(true);
  }, []);


  const handleDuzenle = useCallback((grup) => {
    setDuzenlemeModu(true);
    setAktifGrup(grup);
    setFormKategori(grup.kategori ?? '');
    setFormAciklama(grup.aciklama ?? '');


    const mevcutParcalar = Array.isArray(grup.parcalar)
      ? grup.parcalar.map((parca) => ({
          id: parca.id ?? benzersizId(),
          kod: parca.kod ?? '',
          tedarikci: parca.tedarikci ?? '',
          fiyat: parca.fiyat ?? '',
          adet: parca.adet ?? 1
        }))
      : [];


    setFormParcalar(
      mevcutParcalar.length > 0 ? mevcutParcalar : [bosParca()]
    );


    setModalAcik(true);
  }, []);


  const handleSil = useCallback(
    async (id) => {
      if (silinenGrupId) return;


      if (
        !window.confirm(
          '⚠️ Bu muadil grubunu silmek istediğinizden emin misiniz?'
        )
      ) {
        return;
      }


      try {
        setSilinenGrupId(id);


        const res = await fetch(`/api/muadiller/${id}`, {
          method: 'DELETE'
        });


        if (!res.ok) {
          throw new Error(`Silme işlemi başarısız oldu: ${res.status}`);
        }


        const sonuc = await res.json();


        if (sonuc?.durum === 'silindi') {
          toast.success('✅ Muadil grubu silindi.');
          fetchMuadiller();
        } else {
          throw new Error(sonuc?.hata || 'Silme işlemi başarısız.');
        }
      } catch (err) {
        console.error('Muadil silme hatası:', err);
        toast.error('❌ Muadil grubu silinemedi.');
      } finally {
        setSilinenGrupId(null);
      }
    },
    [silinenGrupId, fetchMuadiller]
  );


  const handleOtomatikGuncelle = useCallback(
    async (grup, senkronize = false) => {
      if (!grup?.id) {
        toast.error('❌ Muadil grubu kimliği bulunamadı.');
        return;
      }


      try {
        setGuncellemeYukleniyor(true);
        setSenkronizeModu(senkronize);


        const res = await fetch(
          `/api/muadiller/etkilenen-kayitlar?muadilGrupId=${encodeURIComponent(
            grup.id
          )}`
        );


        if (!res.ok) {
          throw new Error(
            `Etkilenen kayıtlar alınamadı: ${res.status}`
          );
        }


        const data = await res.json();
        const etkilenenSayi = Number(data?.sayi ?? 0);


        if (etkilenenSayi > 0) {
          setEtkilenenKayitlar({
            sayi: etkilenenSayi,
            kayitlar: Array.isArray(data.kayitlar) ? data.kayitlar : []
          });
          setSonKaydedilenGrup(grup);
          setGuncellemeModalAcik(true);
        } else {
          toast('ℹ️ Bu parça kodlarını kullanan kayıt bulunamadı.', {
            icon: 'ℹ️'
          });
        }
      } catch (err) {
        console.error('Etkilenen kayıt hatası:', err);
        toast.error('❌ Etkilenen kayıtlar alınamadı.');
      } finally {
        setGuncellemeYukleniyor(false);
      }
    },
    []
  );


  const handleTopluGuncelle = useCallback(
    async (islem = 'ekleme') => {
      if (!sonKaydedilenGrup?.id) return;


      if (
        islem === 'senkronize' &&
        !window.confirm(
          '⚠️ Senkronizasyon, muadil grubundan kaldırılan parça kodlarını\n' +
            'eşleşen kompresör kayıtlarından da silecek.\n\n' +
            'Devam etmek istiyor musunuz?'
        )
      ) {
        return;
      }


      try {
        setGuncellemeYukleniyor(true);


        const res = await fetch('/api/muadiller/toplu-guncelle', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            muadilGrupId: sonKaydedilenGrup.id,
            islem,
            hedefKayitlar: 'tum'
          })
        });


        if (!res.ok) {
          throw new Error(`Güncelleme başarısız oldu: ${res.status}`);
        }


        const sonuc = await res.json();


        if (sonuc?.durum === 'ok') {
          const basariliSayi = Number(sonuc?.log?.basarili ?? 0);


          if (basariliSayi > 0) {
            toast.success(
              islem === 'senkronize'
                ? `✅ ${basariliSayi} kayıt senkronize edildi!`
                : `✅ ${basariliSayi} kayıt güncellendi!`
            );
          } else {
            toast('ℹ️ Güncellenecek eşleşen kayıt bulunamadı.', {
              icon: 'ℹ️'
            });
          }


          setGuncellemeModalAcik(false);
          setSonKaydedilenGrup(null);
          setSenkronizeModu(false);
          fetchMuadiller();
        } else {
          throw new Error(sonuc?.hata || 'Güncelleme başarısız.');
        }
      } catch (err) {
        console.error('Toplu güncelleme hatası:', err);
        toast.error('❌ Toplu güncelleme başarısız oldu.');
      } finally {
        setGuncellemeYukleniyor(false);
      }
    },
    [sonKaydedilenGrup, fetchMuadiller]
  );


  const handleKaydet = useCallback(async () => {
    if (kaydediliyor) return;


    if (!formKategori.trim()) {
      toast.error('⚠️ Kategori alanı zorunludur.');
      return;
    }


    const gecerliParcalar = formParcalar.filter((parca) =>
      (parca.kod ?? '').trim()
    );


    if (gecerliParcalar.length === 0) {
      toast.error('⚠️ En az bir parça kodu girilmelidir.');
      return;
    }


    const normalizeEdilmisKodlar = gecerliParcalar.map((parca) =>
      normalizeKod(parca.kod)
    );


    const tekrarKod = normalizeEdilmisKodlar.find(
      (kod, index) => normalizeEdilmisKodlar.indexOf(kod) !== index
    );


    if (tekrarKod) {
      toast.error('⚠️ Aynı parça kodu listede birden fazla kez var.');
      return;
    }


    const bosSatirVar = formParcalar.some(
      (parca) => !(parca.kod ?? '').trim()
    );


    if (bosSatirVar) {
      toast('ℹ️ Boş parça satırları kaydedilmedi.', {
        icon: 'ℹ️'
      });
    }


    if (!duzenlemeModu) {
      const mukerrer = muadiller.find(
        (grup) =>
          normalizeKategori(grup.kategori) ===
          normalizeKategori(formKategori)
      );


      if (mukerrer) {
        const onay = window.confirm(
          `⚠️ "${formKategori}" kategorisi zaten var!\n\nDevam edilsin mi?`
        );


        if (!onay) return;
      }
    }


    const veri = {
      kategori: formKategori.trim(),
      aciklama: formAciklama.trim(),
      parcalar: gecerliParcalar.map((parca) => ({
        kod: (parca.kod ?? '').trim(),
        tedarikci: (parca.tedarikci ?? '').trim(),
        fiyat: Number.parseFloat(parca.fiyat) || 0,
        adet: Number.parseInt(parca.adet, 10) || 1
      }))
    };


    try {
      setKaydediliyor(true);


      const url =
        duzenlemeModu && aktifGrup?.id
          ? `/api/muadiller/${aktifGrup.id}`
          : '/api/muadiller';


      const method = duzenlemeModu ? 'PUT' : 'POST';


      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(veri)
      });


      if (!res.ok) {
        throw new Error(`Kayıt işlemi başarısız oldu: ${res.status}`);
      }


      const sonuc = await res.json();


      if (sonuc?.durum === 'ok' || sonuc?.data) {
        toast.success(
          `✅ ${duzenlemeModu ? 'Güncellendi' : 'Eklendi'}!`
        );


        setModalAcik(false);
        fetchMuadiller();


        if (!duzenlemeModu && sonuc?.data?.id) {
          const yeniGrup = sonuc.data;


          setTimeout(() => {
            handleOtomatikGuncelle(yeniGrup, false);
          }, 500);
        }
      } else {
        throw new Error(sonuc?.hata || 'Kayıt işlemi başarısız.');
      }
    } catch (err) {
      console.error('Muadil kaydetme hatası:', err);
      toast.error('❌ Muadil grubu kaydedilemedi.');
    } finally {
      setKaydediliyor(false);
    }
  }, [
    kaydediliyor,
    formKategori,
    formAciklama,
    formParcalar,
    duzenlemeModu,
    aktifGrup,
    muadiller,
    fetchMuadiller,
    handleOtomatikGuncelle
  ]);


  const handleParcaEkle = useCallback(() => {
    if (!yeniParcaKod.trim()) {
      toast.error('⚠️ Parça kodu girilmelidir.');
      return;
    }


    const yeniKod = normalizeKod(yeniParcaKod);


    const mukerrer = formParcalar.some(
      (parca) => normalizeKod(parca.kod) === yeniKod
    );


    if (mukerrer) {
      toast.error('⚠️ Bu parça kodu listede zaten var.');
      return;
    }


    setFormParcalar((prev) => [
      ...prev,
      {
        ...bosParca(),
        kod: yeniParcaKod.trim(),
        tedarikci: yeniParcaTedarikci.trim(),
        fiyat: yeniParcaFiyat || '',
        adet: yeniParcaAdet || 1
      }
    ]);


    setYeniParcaKod('');
    setYeniParcaTedarikci('');
    setYeniParcaFiyat('');
    setYeniParcaAdet('1');
  }, [
    yeniParcaKod,
    yeniParcaTedarikci,
    yeniParcaFiyat,
    yeniParcaAdet,
    formParcalar
  ]);


  const handleParcaSil = useCallback((parcaId) => {
    setFormParcalar((prev) =>
      prev.filter((parca) => parca.id !== parcaId)
    );
  }, []);


  const handleParcaDegisiklik = useCallback((parcaId, field, value) => {
    setFormParcalar((prev) =>
      prev.map((parca) =>
        parca.id === parcaId
          ? {
              ...parca,
              [field]: value
            }
          : parca
      )
    );
  }, []);


  const handleModalKapat = useCallback(() => {
    setModalAcik(false);
    setDuzenlemeModu(false);
    setAktifGrup(null);
    setFormKategori('');
    setFormAciklama('');
    setFormParcalar([bosParca()]);
  }, []);

  useEffect(() => {
  if (!modalAcik) return;


  const escapeDinleyici = (event) => {
    if (event.key === 'Escape') {
      handleModalKapat();
    }
  };


  window.addEventListener('keydown', escapeDinleyici);


  return () => {
    window.removeEventListener('keydown', escapeDinleyici);
  };
}, [modalAcik, handleModalKapat]);


  if (yukleniyor) {
    return (
      <div className="muadil-yonetim-wrapper">
        <div className="muadil-yukleniyor">
          <div className="loading-spinner"></div>
          <p>⏳ Muadil verileri yükleniyor...</p>
        </div>
      </div>
    );
  }


  return (
    <div className="muadil-yonetim-wrapper">
      <Card className="muadil-card">
        <CardContent>
          <div className="muadil-header">
            <h2 className="muadil-title">🔁 Muadil Parça Yönetimi</h2>


            <Button
              onClick={handleYeniAc}
              size="sm"
              className="btn-yeni-grup"
            >
              <Plus className="w-4 h-4 mr-1" />
              Yeni Grup Ekle
            </Button>
          </div>


          <div className="muadil-filtreler">
            <div className="filtre-arama">
              <p className="filtre-label">Arama</p>


              <div className="arama-wrapper">
                <Search className="arama-icon" />


                <Input
                  type="search"
                  placeholder="Kod, tedarikçi veya açıklama ara..."
                  value={arama}
                  onChange={(e) => setArama(e.target.value)}
                  className="arama-input"
                />
              </div>
            </div>


            <div className="filtre-kategori">
              <p className="filtre-label">Kategori</p>


              <select
                value={seciliKategori}
                onChange={(e) => setSeciliKategori(e.target.value)}
                className="kategori-select"
              >
                <option value="tum">📋 Tüm Kategoriler</option>


                {varsayilanKategoriler.map((kategori) => (
                  <option key={kategori} value={kategori}>
                    {kategori}
                  </option>
                ))}
              </select>
            </div>
          </div>


          <div className="muadil-liste">
            {filtrelenmisMuadiller.length === 0 ? (
              <div className="muadil-bos">
                {arama || seciliKategori !== 'tum'
                  ? '🔍 Filtreye uygun kayıt bulunamadı.'
                  : '📭 Henüz muadil grup eklenmemiş.'}
              </div>
            ) : (
              filtrelenmisMuadiller.map((grup) => (
                <Card key={grup.id} className="muadil-grup-kart">
                  <CardContent className="grup-kart-content">
                    <div className="grup-kart-header">
                      <div className="grup-bilgi">
                        <h4 className="grup-kategori">
                          {grup.kategori}
                        </h4>


                        <p className="grup-aciklama">
                          {grup.aciklama || '—'}
                        </p>


                        <p className="grup-meta">
                          📦{' '}
                          {Array.isArray(grup.parcalar)
                            ? grup.parcalar.length
                            : 0}{' '}
                          parça • 🕐{' '}
                          {gecerliTarih(grup.createdAt)
                            ? new Date(
                                grup.createdAt
                              ).toLocaleDateString('tr-TR')
                            : '—'}
                        </p>
                      </div>


                      <div className="grup-islemler">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDuzenle(grup)}
                          title="Düzenle"
                          aria-label={`${grup.kategori} muadil grubunu düzenle`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>


                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleOtomatikGuncelle(grup, false)
                          }
                          title="Mevcut kayıtlara eksikleri ekle"
                          className="btn-guncelle"
                          aria-label={`${grup.kategori} muadillerini mevcut kayıtlara ekle`}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>


                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleOtomatikGuncelle(grup, true)
                          }
                          title="Mevcut kayıtları senkronize et"
                          className="btn-senkronize"
                          aria-label={`${grup.kategori} muadillerini mevcut kayıtlarla senkronize et`}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>


                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleSil(grup.id)}
                          disabled={silinenGrupId === grup.id}
                          title="Sil"
                          aria-label={`${grup.kategori} muadil grubunu sil`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>


                    <div className="parca-onizleme">
                      {(Array.isArray(grup.parcalar)
                        ? grup.parcalar
                        : []
                      )
                        .slice(0, 5)
                        .map((parca, index) => (
                          <span
                            key={parca.id ?? `${grup.id}-${index}`}
                            className="muadil-kod-chip"
                          >
                            {parca.kod}
                          </span>
                        ))}


                      {(Array.isArray(grup.parcalar)
                        ? grup.parcalar.length
                        : 0) > 5 && (
                        <span className="daha-fazla">
                          +
                          {(Array.isArray(grup.parcalar)
                            ? grup.parcalar.length
                            : 0) - 5}{' '}
                          daha
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>


      {modalAcik && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="muadil-modal-baslik"
          ref={modalRef}
          onClick={() => {
            const onay = window.confirm(
              '⚠️ Değişiklikler kaybolacak. Kapatmak istiyor musunuz?'
            );


            if (onay) {
              handleModalKapat();
            }
          }}
        >
          <div
            className="modal-content muadil-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              id="muadil-modal-baslik"
              className="modal-baslik"
            >
              {duzenlemeModu
                ? '✏️ Muadil Grubu Düzenle'
                : '➕ Yeni Muadil Grubu Ekle'}
            </h3>


            <div className="modal-body">
              <div className="kategori-aciklama-grid">
                <div className="form-group">
                  <p className="form-label">Kategori *</p>


                  <select
                    value={formKategori}
                    onChange={(e) => setFormKategori(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Seçiniz</option>


                    {varsayilanKategoriler.map((kategori) => (
                      <option key={kategori} value={kategori}>
                        {kategori}
                      </option>
                    ))}
                  </select>
                </div>


                <div className="form-group">
                  <p className="form-label">Açıklama</p>


                  <Input
                    placeholder="Örn: Lupamat VK-1560.00 ve Muadilleri"
                    value={formAciklama}
                    onChange={(e) => setFormAciklama(e.target.value)}
                  />
                </div>
              </div>


              <div className="parca-listesi-bolumu">
                <p className="form-label">Parça Listesi</p>


                {formParcalar.map((parca) => (
                  <div
                    key={parca.id}
                    className="parca-satir"
                  >
                    <Input
                      placeholder="Parça Kodu *"
                      value={parca.kod}
                      onChange={(e) =>
                        handleParcaDegisiklik(
                          parca.id,
                          'kod',
                          e.target.value
                        )
                      }
                      className="parca-input"
                    />


                    <Input
                      placeholder="Tedarikçi"
                      value={parca.tedarikci || ''}
                      onChange={(e) =>
                        handleParcaDegisiklik(
                          parca.id,
                          'tedarikci',
                          e.target.value
                        )
                      }
                      className="parca-input"
                    />


                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Fiyat"
                      value={parca.fiyat || ''}
                      onChange={(e) =>
                        handleParcaDegisiklik(
                          parca.id,
                          'fiyat',
                          e.target.value
                        )
                      }
                      className="parca-input"
                    />


                    <Input
                      type="number"
                      min="1"
                      placeholder="Adet"
                      value={parca.adet || 1}
                      onChange={(e) =>
                        handleParcaDegisiklik(
                          parca.id,
                          'adet',
                          e.target.value
                        )
                      }
                      className="parca-input"
                    />


                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleParcaSil(parca.id)}
                      className="btn-sil-parca"
                      aria-label="Parça satırını sil"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>


              <Card className="yeni-parca-card">
                <CardContent className="yeni-parca-content">
                  <p className="yeni-parca-baslik">
                    ➕ Yeni Parça Ekle
                  </p>


                  <div className="yeni-parca-grid">
                    <div>
                      <p className="form-label-small">Kod *</p>


                      <Input
                        placeholder="Örn: MANN W962/2"
                        value={yeniParcaKod}
                        onChange={(e) =>
                          setYeniParcaKod(e.target.value)
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleParcaEkle();
                          }
                        }}
                      />
                    </div>


                    <div>
                      <p className="form-label-small">Tedarikçi</p>


                      <Input
                        placeholder="Örn: Mann+Hummel"
                        value={yeniParcaTedarikci}
                        onChange={(e) =>
                          setYeniParcaTedarikci(e.target.value)
                        }
                      />
                    </div>


                    <div>
                      <p className="form-label-small">Fiyat</p>


                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={yeniParcaFiyat}
                        onChange={(e) =>
                          setYeniParcaFiyat(e.target.value)
                        }
                      />
                    </div>


                    <div>
                      <p className="form-label-small">Adet</p>


                      <Input
                        type="number"
                        min="1"
                        value={yeniParcaAdet}
                        onChange={(e) =>
                          setYeniParcaAdet(e.target.value)
                        }
                      />
                    </div>


                    <Button
                      type="button"
                      size="sm"
                      onClick={handleParcaEkle}
                      className="btn-ekle-parca"
                    >
                      Ekle
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>


            <div className="modal-footer">
              <Button
                variant="outline"
                onClick={handleModalKapat}
              >
                İptal
              </Button>


              <Button
                onClick={handleKaydet}
                disabled={kaydediliyor}
              >
                <Save className="w-4 h-4 mr-1" />
                {kaydediliyor
                  ? '⏳ Kaydediliyor...'
                  : duzenlemeModu
                  ? 'Güncelle'
                  : 'Kaydet'}
              </Button>
            </div>
          </div>
        </div>
      )}


      {guncellemeModalAcik && sonKaydedilenGrup && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="otomatik-guncelleme-baslik"
          onClick={() => setGuncellemeModalAcik(false)}
        >
          <div
            className="modal-content guncelleme-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              id="otomatik-guncelleme-baslik"
              className="modal-baslik"
            >
              <RefreshCw className="w-5 h-5 inline mr-2" />
              {senkronizeModu
                ? 'Muadilleri Senkronize Et'
                : 'Otomatik Muadil Güncelleme'}
            </h3>


            <div className="modal-body">
              <div className="guncelleme-bilgi">
                <p className="guncelleme-aciklama">
                  <strong>"{sonKaydedilenGrup.kategori}"</strong>{' '}
                  kategorisindeki muadiller{' '}
                  <strong>
                    {etkilenenKayitlar.sayi}
                  </strong>{' '}
                  kompresör kaydına uygulanabilir.
                </p>


                {senkronizeModu ? (
                  <p className="guncelleme-uyari">
                    ⚠️ Senkronize et seçildiğinde, muadil grubundan
                    kaldırılan kodlar eşleşen kompresör kayıtlarından
                    da silinecek.
                  </p>
                ) : (
                  <p className="guncelleme-uyari">
                    ℹ️ Bu işlem yalnızca eksik parça kodlarını ekler;
                    mevcut kodları silmez veya değiştirmez.
                  </p>
                )}
              </div>


              {etkilenenKayitlar.sayi > 0 && (
                <div className="etkilenen-liste">
                  <p className="liste-baslik">
                    Etkilenecek Kayıtlar:
                  </p>


                  <div className="kayit-grid">
                    {etkilenenKayitlar.kayitlar
                      .slice(0, 10)
                      .map((kayit, index) => (
                        <div
                          key={
                            kayit.id ??
                            `${kayit.firma}-${kayit.model}-${kayit.seriNo}-${index}`
                          }
                          className="kayit-item"
                        >
                          <span className="kayit-firma">
                            {kayit.firma}
                          </span>


                          <span className="kayit-model">
                            {kayit.model}
                          </span>


                          <span className="kayit-seri">
                            {kayit.seriNo}
                          </span>
                        </div>
                      ))}


                    {etkilenenKayitlar.sayi > 10 && (
                      <p className="daha-fazla-kayit">
                        ...ve {etkilenenKayitlar.sayi - 10} kayıt
                        daha
                      </p>
                    )}
                  </div>
                </div>
              )}


              {guncellemeYukleniyor && (
                <div className="guncelleme-progress">
                  <div className="loading-spinner small"></div>
                  <p>Kayıtlar güncelleniyor...</p>
                </div>
              )}
            </div>


            <div className="modal-footer">
              <Button
                variant="outline"
                onClick={() => {
                  setGuncellemeModalAcik(false);
                  setSonKaydedilenGrup(null);
                  setSenkronizeModu(false);
                }}
              >
                ❌ Atla
              </Button>


              {senkronizeModu ? (
                <Button
                  variant="destructive"
                  onClick={() => handleTopluGuncelle('senkronize')}
                  disabled={guncellemeYukleniyor}
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  🔄 Senkronize Et ({etkilenenKayitlar.sayi})
                </Button>
              ) : (
                <Button
                  onClick={() => handleTopluGuncelle('ekleme')}
                  disabled={guncellemeYukleniyor}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  ✅ Eksikleri Ekle ({etkilenenKayitlar.sayi})
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default MuadilYonetim;